import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface UserRole {
  isAdmin: boolean;
  isCoach: boolean;
  roles: string[];
  loading: boolean;
}

const ROLE_SESSION_TIMEOUT_MS = 2_500;

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T | null> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<null>((resolve) => {
    timeoutId = setTimeout(() => resolve(null), timeoutMs);
  });

  return Promise.race([promise, timeout]).finally(() => {
    if (timeoutId) clearTimeout(timeoutId);
  });
}

/**
 * Hook to fetch and manage user roles from the database
 * Uses server-side role checks to prevent client-side manipulation
 */
export function useUserRole(): UserRole {
  const [roles, setRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserRoles = async () => {
      try {
        const result = await withTimeout(supabase.auth.getSession(), ROLE_SESSION_TIMEOUT_MS);
        const session = result?.data?.session ?? null;
        
        if (!session) {
          setRoles([]);
          setLoading(false);
          return;
        }

        // Call the SECURITY DEFINER function to get user roles
        const { data, error } = await supabase.rpc('get_user_roles');
        
        if (error) {
          console.error('Error fetching user roles:', error);
          setRoles([]);
        } else {
          // Extract role strings from the returned data
          const userRoles = data?.map((r: { role: string }) => r.role) || [];
          setRoles(userRoles);
        }
      } catch (error) {
        console.error('Error in fetchUserRoles:', error);
        setRoles([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUserRoles();

    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      fetchUserRoles();
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return {
    isAdmin: roles.includes('admin'),
    isCoach: roles.includes('coach'),
    roles,
    loading,
  };
}
